import { create } from 'zustand';

import { MiniAppFetchError } from '../_shared/fetch-error';
import { isAbortedRequest, isMalformedResponse } from '../_shared/fetch-json';

/**
 * Per-campaign state of the LIVE member resync `PlayFlowShell` runs when a
 * play is created or opened (plans/2026-09-17-playbooks-live-member-sync-plan.md).
 *
 * Why a store and not a ref: the shell must fire the sync exactly once per
 * REAL mount. A cleanup-only ref is the classic StrictMode casualty
 * (docs/v4-system/strictmode-resilient-effects.md) and a reset-on-mount ref
 * would fire twice under the dev setup→cleanup→setup double-invoke. Keying the
 * guard on module-scope state instead — `beginMemberSync` returns `false`
 * while a sync for that campaign is already in flight — makes the second
 * StrictMode setup a no-op join, with no ref bookkeeping at all (the same
 * idempotent-by-state shape `flow-session-store.ts` uses in the same shell).
 *
 * `mountKey` is what makes a REMOUNT of the same play resync and re-gate:
 * an entry that settled for a previous mount (different key) reads as
 * `'syncing'` to the new mount until its own sync settles, so the previous
 * visit's cached roster is never rendered first.
 */
export type MemberSyncEntry =
  | { status: 'syncing'; mountKey: string }
  | { status: 'synced'; mountKey: string; memberCount: number; syncedAt: string }
  | { status: 'failed'; mountKey: string; detail: string | null };

export interface MemberSyncState {
  byCampaign: Record<string, MemberSyncEntry>;
}

/** Exported per the store convention — reset idiom is `useMemberSyncStore.setState(initialState, true)`. */
export const initialState: MemberSyncState = { byCampaign: {} };

export const useMemberSyncStore = create<MemberSyncState>(() => ({ ...initialState }));

let mountSeq = 0;
/** One key per `PlayFlowShell` mount — `useState(newMemberSyncMountKey)`; never `useId` (tree-position based, repeats on remount). */
export function newMemberSyncMountKey(): string {
  mountSeq += 1;
  return `member-sync-mount-${mountSeq}`;
}

/**
 * Atomically claim the sync for `campaignSfdcId`. Returns `true` when the
 * caller must issue the request; `false` when one is already in flight (the
 * caller joins it — StrictMode's second setup, or a fresh mount arriving while
 * a previous mount's request is still running). Joining adopts the entry onto
 * the new `mountKey` so the joiner is un-gated when that request settles.
 */
export function beginMemberSync(campaignSfdcId: string, mountKey: string): boolean {
  const prev = useMemberSyncStore.getState().byCampaign[campaignSfdcId];
  if (prev?.status === 'syncing') {
    useMemberSyncStore.setState((s) => ({
      byCampaign: { ...s.byCampaign, [campaignSfdcId]: { ...prev, mountKey } },
    }));
    return false;
  }
  useMemberSyncStore.setState((s) => ({
    byCampaign: { ...s.byCampaign, [campaignSfdcId]: { status: 'syncing', mountKey } },
  }));
  return true;
}

export function completeMemberSync(
  campaignSfdcId: string,
  result: { memberCount: number; syncedAt: string },
): void {
  useMemberSyncStore.setState((s) => {
    const prev = s.byCampaign[campaignSfdcId];
    if (!prev) return s;
    return {
      byCampaign: {
        ...s.byCampaign,
        [campaignSfdcId]: { status: 'synced', mountKey: prev.mountKey, ...result },
      },
    };
  });
}

export function failMemberSync(campaignSfdcId: string, detail: string | null): void {
  useMemberSyncStore.setState((s) => {
    const prev = s.byCampaign[campaignSfdcId];
    if (!prev) return s;
    return {
      byCampaign: { ...s.byCampaign, [campaignSfdcId]: { status: 'failed', mountKey: prev.mountKey, detail } },
    };
  });
}

export function selectMemberSyncEntry(campaignSfdcId: string) {
  return (s: MemberSyncState): MemberSyncEntry | undefined => s.byCampaign[campaignSfdcId];
}

/** What the shell renders. Anything not settled FOR THIS MOUNT is `'syncing'`. */
export type MemberSyncPhase =
  | { kind: 'syncing' }
  | { kind: 'ready' }
  | { kind: 'failed'; detail: string | null };

export function memberSyncPhase(entry: MemberSyncEntry | undefined, mountKey: string): MemberSyncPhase {
  if (entry === undefined || entry.mountKey !== mountKey || entry.status === 'syncing') return { kind: 'syncing' };
  if (entry.status === 'failed') return { kind: 'failed', detail: entry.detail };
  return { kind: 'ready' };
}

/**
 * Rep-facing detail for the fallback band. The server's `problem()` message
 * when there is one (`MiniAppFetchError.userMessage` — `detail` → `title`),
 * a fixed sentence for the client timeout, otherwise nothing. Never a status
 * code, never a raw stack message.
 */
export function memberSyncFailureDetail(err: unknown): string | null {
  // fetchJson turns the client timeout into an `aborted` FetchJsonError, which is also a MiniAppFetchError: test it first.
  if (isAbortedRequest(err)) return 'The refresh took longer than two minutes.';
  if (isMalformedResponse(err)) return null;
  if (err instanceof MiniAppFetchError) return err.userMessage ?? null;
  if (err instanceof Error && err.name === 'AbortError') return 'The refresh took longer than two minutes.';
  return null;
}
